/**
 * Sent — what actually went out (FR-K5), as the kit's `DataTable` (Wave 15E),
 * with a filter by what happened and a search by template.
 *
 * The row that did not go is the only one anybody reads this table for, so the
 * reason is on it, in the firm's words (`explainReason`).
 */

import { Badge, EmptyState, formatDateTime } from '@fiveox/ui'
import { useMemo } from 'react'

import { type ListColumn, ListTable, ListToolbar, useListTable } from '../../components/page'
import { type DeliveryRecord, explainReason } from '../../lib/api/comms'
import { SOURCE_LABELS } from './shared'

function DeliveryBadge({ status }: { status: DeliveryRecord['status'] }) {
  if (status === 'sent') return <Badge tone="success">Sent</Badge>
  if (status === 'failed') return <Badge tone="error">Not sent</Badge>
  if (status === 'skipped') return <Badge tone="warning">Not sent</Badge>
  return <Badge>{status}</Badge>
}

const OUTCOMES: Array<{ value: string; label: string; match: (d: DeliveryRecord) => boolean }> = [
  { value: 'sent', label: 'Sent', match: (d) => d.status === 'sent' },
  { value: 'not_sent', label: 'Not sent', match: (d) => d.status === 'failed' || d.status === 'skipped' },
  { value: 'other', label: 'Waiting or in the portal', match: (d) => d.status === 'pending' || d.status === 'posted' },
]

export function DeliveryList({ deliveries }: { deliveries: DeliveryRecord[] }) {
  const columns = useMemo<ListColumn<DeliveryRecord>[]>(
    () => [
      {
        key: 'when',
        label: 'When',
        width: 170,
        phone: true,
        sortField: 'created_at',
        render: (d) => <span className="whitespace-nowrap">{formatDateTime(d.sent_at ?? d.created_at)}</span>,
      },
      {
        key: 'template_code',
        label: 'Template',
        width: 200,
        sortField: 'template_code',
        render: (d) => <span className="font-mono text-xs">{d.template_code}</span>,
      },
      {
        key: 'source_kind',
        label: 'Why',
        width: 160,
        sortField: 'source_kind',
        render: (d) => <span className="text-xs">{SOURCE_LABELS[d.source_kind]}</span>,
      },
      {
        key: 'status',
        label: 'What happened',
        width: 260,
        phone: true,
        sortField: 'status',
        render: (d) => (
          <div className="min-w-0">
            <DeliveryBadge status={d.status} />
            {d.status !== 'sent' && d.reason && (
              <p className="text-muted-foreground mt-1 max-w-[10rem] text-xs whitespace-normal sm:max-w-xs">
                {explainReason(d.reason)}
              </p>
            )}
          </div>
        ),
      },
    ],
    [],
  )

  const list = useListTable(deliveries, {
    columns,
    search: (d) => [d.template_code, SOURCE_LABELS[d.source_kind]],
    filters: [
      {
        key: 'outcome',
        label: 'What happened',
        allLabel: 'Anything',
        options: OUTCOMES.map(({ value, label }) => ({ value, label })),
        match: (d, value) => OUTCOMES.find((o) => o.value === value)?.match(d) ?? true,
      },
    ],
    initialSort: { field: 'created_at', dir: 'desc' },
  })

  if (deliveries.length === 0) {
    return <EmptyState variant="outline" title="Nothing has been sent yet" />
  }

  return (
    <div className="space-y-3">
      <ListToolbar list={list} placeholder="Search by template" />
      <ListTable list={list} loading={false} rowKey={(d) => d.id} />
    </div>
  )
}
